import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Wallet, Info, CheckCircle2, AlertTriangle, Lock, Clock, ExternalLink, Copy, Link2, FileText } from "lucide-react";
import { toast } from "sonner";
import { FirstInvoiceDialog } from "@/components/superadmin/FirstInvoice";

// Předplatné fakturou (206): karta v detailu firmy (Superadmin › Správa) + napojení Fakturoid.
// Data počítá backend (company.billing): fáze ok / invoice_due / unpaid / locked, další období, zámek.

const CYCLES = [["month", "Měsíčně", 1], ["half", "Pololetně", 6], ["year", "Ročně", 12]];
const PERIOD_WORD = { month: "měsíc", half: "pololetí", year: "rok" };
const day = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${d}. ${m}. ${y}`;
};
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const plusDays = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

export function InfoTip({ children, label = "Vysvětlivka" }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label} className="w-7 h-7 -my-1 rounded-full grid place-items-center text-muted-foreground hover:text-foreground shrink-0">
          <Info size={15} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm leading-relaxed">{children}</PopoverContent>
    </Popover>
  );
}

function Banner({ tone, icon: Icon, children, testid }) {
  const cls = {
    ok: "bg-card border-border",
    brand: "bg-brand/10 border-brand/30",
    warn: "bg-amber-500/10 border-amber-500/40",
    danger: "bg-rose-500/10 border-rose-500/40",
  }[tone];
  const ic = { ok: "text-success", brand: "text-brand", warn: "text-amber-600 dark:text-amber-400", danger: "text-rose-600 dark:text-rose-400" }[tone];
  return (
    <div className={`rounded-[12px] border px-3.5 py-3 flex gap-2.5 text-sm ${cls}`} data-testid={testid}>
      <Icon size={17} className={`shrink-0 mt-0.5 ${ic}`} aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function statusBanner(b, amount) {
  const ni = b.next_invoice && b.next_invoice.for === b.paid_until ? b.next_invoice : null;
  const inv = ni && (
    <a href={ni.url || ni.public_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-brand hover:underline">
      Faktura č. {ni.number}{ni.status === "sent" ? " · odeslána" : ni.status === "paid" ? " · zaplacena" : " · neodeslaná"} <ExternalLink size={12} />
    </a>
  );
  if (b.phase === "first_due") {
    return (
      <Banner tone="brand" icon={Clock} testid="sub-banner-first">
        <b>Čeká na první platbu</b> — splatnost <b>{day(b.first_due)}</b>. Když nezaplatí, {day(plusDays(b.first_due, 1))} dostane majitel upozornění a {day(plusDays(b.first_due, 4))} se přístup zamkne.
        {inv && <div className="mt-1">{inv}</div>}
      </Banner>
    );
  }
  if (!b.paid_until) {
    return <Banner tone="warn" icon={AlertTriangle} testid="sub-banner-missing"><b>Doplň „Zaplaceno do“.</b> Bez něj se nic nehlídá — žádné připomínky ani zámek.</Banner>;
  }
  if (b.phase === "locked") {
    return <Banner tone="danger" icon={Lock} testid="sub-banner-locked"><b>Zamčeno od {day(b.lock_on)} — nezaplaceno.</b> Po zaevidování platby se firma hned odemkne.{inv && <div className="mt-1">{inv}</div>}</Banner>;
  }
  if (b.phase === "unpaid") {
    return (
      <Banner tone="warn" icon={Clock} testid="sub-banner-unpaid">
        <b>Čeká na úhradu</b> — období od {day(b.next_start)}.{" "}
        {b.lock_postponed_until ? <>Zámek odložen do <b>{day(b.lock_postponed_until)}</b>.</>
          : b.lock_on ? <>Majitel upozorněn, zámek <b>{day(b.lock_on)}</b>.</>
          : <>Majitel dostane upozornění, 3 dny poté zámek.</>}
        {inv && <div className="mt-1">{inv}</div>}
      </Banner>
    );
  }
  if (b.phase === "invoice_due") {
    return (
      <Banner tone="brand" icon={FileText} testid="sub-banner-due">
        <b>{ni ? "Faktura na další období" : "Vystavit fakturu"}</b> — {day(b.next_start)} – {day(b.next_end)} · {formatCZK(amount)} · splatnost {day(b.next_start)}.
        {inv && <div className="mt-1">{inv}</div>}
      </Banner>
    );
  }
  return (
    <Banner tone="ok" icon={CheckCircle2} testid="sub-banner-ok">
      Zaplaceno do <b>{day(b.paid_until)}</b>. Faktura na další období ({formatCZK(amount)}) se vystaví <b>{day(b.remind_on)}</b>.
    </Banner>
  );
}

export function SubscriptionCard({ c, price, plans, faConfigured, onChanged }) {
  const b = c.billing || {};
  const stripe = b.mode === "stripe" || !!c.stripe_subscription_id;
  const [form, setForm] = useState({ paid_since: "", paid_until: "", billing_cycle: "month" });
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [firstOpen, setFirstOpen] = useState(false);
  // 325: firma bez zaplaceného období (demo / nově domluvená) → převést na klienta s první fakturou
  const canFirst = b.phase !== "first_due" && !b.paid_until;
  const init = { paid_since: String(c.paid_since || "").slice(0, 10), paid_until: b.paid_until || "", billing_cycle: b.cycle || "month" };
  useEffect(() => { setForm(init); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, c.paid_since, b.paid_until, b.cycle]);
  const dirty = form.paid_since !== init.paid_since || form.paid_until !== init.paid_until || form.billing_cycle !== init.billing_cycle;
  const months = (CYCLES.find(([k]) => k === (form.billing_cycle || "month")) || CYCLES[0])[2];
  const amount = (price || 0) * months;
  const past = form.paid_until && form.paid_until < todayIso();

  const post = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(path, body); toast.success(ok); await onChanged?.(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const save = () => {
    const body = {};
    if (form.paid_since !== init.paid_since) body.paid_since = form.paid_since || null;
    if (form.paid_until !== init.paid_until) body.paid_until = form.paid_until || null;
    if (form.billing_cycle !== init.billing_cycle) body.billing_cycle = form.billing_cycle;
    if (!b.mode) body.billing_mode = "invoice";
    post(`/admin/companies/${c.id}/billing/settings`, body, "Předplatné uloženo");
  };

  if (stripe) {
    return (
      <div className="bg-card border border-border rounded-[18px] p-4 space-y-2" data-testid="section-subscription">
        <div className="text-sm font-bold flex items-center gap-1.5"><Wallet size={16} className="text-muted-foreground" aria-hidden="true" /> Předplatné</div>
        <p className="text-sm">Platí <b>kartou přes Stripe</b> — období, platby i upomínky řídí Stripe. Další platba {c.next_billing ? <b>{day(c.next_billing)}</b> : "—"}.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-[18px] p-4 space-y-3" data-testid="section-subscription">
      <div className="flex items-center gap-1.5">
        <div className="text-sm font-bold flex items-center gap-1.5 flex-1"><Wallet size={16} className="text-muted-foreground" aria-hidden="true" /> Předplatné · fakturou</div>
        <InfoTip label="Jak funguje předplatné fakturou">
          Nový klient: „Převést na klienta“ — vybereš první fakturu (z Fakturoidu nebo ručně); do její splatnosti mají plný přístup, den po splatnosti přijde majiteli upozornění a za 3 dny zámek.{" "}
          7 dní před koncem období se vystaví faktura (s napojeným Fakturoidem sama, jinak ti přijde e-mail „Vystav fakturu“).
          Když první den nového období není platba zaevidovaná, majitel dostane jeden e-mail a <b>za 3 dny</b> se přístup zamkne.
          Po zaevidování platby se hned odemkne.
        </InfoTip>
      </div>
      {statusBanner(b, (price || 0) * ((CYCLES.find(([k]) => k === (b.cycle || "month")) || CYCLES[0])[2]))}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div className="min-w-0">
          <Label className="text-xs" htmlFor="sub-since">Spolupráce od</Label>
          <Input id="sub-since" type="date" value={form.paid_since} onChange={(e) => setForm({ ...form, paid_since: e.target.value })}
            className="h-10 bg-card w-full min-w-0 mt-1" data-testid="sub-paid-since" />
        </div>
        <div className="min-w-0">
          <Label className="text-xs" htmlFor="sub-until">Zaplaceno do</Label>
          <Input id="sub-until" type="date" value={form.paid_until} onChange={(e) => setForm({ ...form, paid_until: e.target.value })}
            className="h-10 bg-card w-full min-w-0 mt-1" data-testid="sub-paid-until" />
        </div>
      </div>
      {past && form.paid_until !== init.paid_until && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="sub-past-warning">
          Datum je v minulosti — zítra přijde majiteli upozornění na neuhrazenou platbu a za další 3 dny zámek.
        </p>
      )}
      <div>
        <div className="text-xs font-medium mb-1">Období</div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 p-1 rounded-full bg-card border border-border w-fit" role="radiogroup" aria-label="Období" data-testid="sub-cycle">
            {CYCLES.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={form.billing_cycle === k} onClick={() => setForm({ ...form, billing_cycle: k })}
                data-testid={`sub-cycle-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${form.billing_cycle === k ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{formatCZK(amount)} za {PERIOD_WORD[form.billing_cycle]} · neplátce DPH</span>
        </div>
      </div>
      {(dirty || !b.mode) && (
        <Button size="sm" disabled={busy || !dirty && !!b.mode} onClick={save} className="w-full md:w-auto" data-testid="sub-save">Uložit předplatné</Button>
      )}

      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        {canFirst && (
          <Button size="sm" disabled={busy} onClick={() => setFirstOpen(true)} className="w-full sm:w-auto font-bold" data-testid="sub-first-btn">Převést na klienta · první faktura</Button>
        )}
        <Button size="sm" variant={canFirst ? "outline" : "default"} disabled={busy} onClick={() => setPayOpen(true)} className={`w-full sm:w-auto ${canFirst ? "" : "font-bold"}`} data-testid="sub-pay-btn">Zaevidovat platbu</Button>
        {(b.phase === "unpaid" || b.phase === "locked") && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setPostOpen(true)} className="w-full sm:w-auto" data-testid="sub-postpone-btn">
            {b.lock_postponed_until ? `Odklad do ${day(b.lock_postponed_until)}` : "Odložit zámek"}
          </Button>
        )}
      </div>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} c={c} b={b} amount={(price || 0) * ((CYCLES.find(([k]) => k === (b.cycle || "month")) || CYCLES[0])[2])}
        onSubmit={async (body) => (await post(`/admin/companies/${c.id}/billing/payment`, body, "Platba zaevidována")) && setPayOpen(false)} busy={busy} />
      <FirstInvoiceDialog open={firstOpen} onOpenChange={setFirstOpen} c={c} plans={plans} faConfigured={faConfigured}
        onDone={async (ok, err) => { if (err) { toast.error(err); return; } toast.success(ok); await onChanged?.(); }} />
      <PostponeDialog open={postOpen} onOpenChange={setPostOpen} b={b} busy={busy}
        onSubmit={async (until) => (await post(`/admin/companies/${c.id}/billing/postpone`, { until }, until ? "Zámek odložen" : "Odklad zrušen")) && setPostOpen(false)} />
    </div>
  );
}

export function PaymentDialog({ open, onOpenChange, c, b, amount, onSubmit, busy }) {
  const ni = b.next_invoice && b.next_invoice.for === b.paid_until ? b.next_invoice : null;
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ paid_on: todayIso(), amount: String(ni?.total ?? amount ?? ""), invoice_no: ni?.number || "", custom: false, paid_until: "" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const target = f.custom ? f.paid_until : b.next_end;
  const submit = () => {
    const body = { paid_on: f.paid_on, amount: f.amount === "" ? null : Number(f.amount), invoice_no: f.invoice_no || null };
    if (f.custom && f.paid_until) body.paid_until = f.paid_until;
    onSubmit(body);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sub-pay-dialog">
        <DialogHeader>
          <DialogTitle>Zaevidovat platbu</DialogTitle>
          <DialogDescription>{c.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="min-w-0">
              <Label className="text-xs" htmlFor="pay-on">Datum platby</Label>
              <Input id="pay-on" type="date" value={f.paid_on || ""} onChange={(e) => setF({ ...f, paid_on: e.target.value })} className="h-11 w-full min-w-0 mt-1" data-testid="pay-on" />
            </div>
            <div className="min-w-0">
              <Label className="text-xs" htmlFor="pay-amount">Částka (Kč)</Label>
              <Input id="pay-amount" type="number" inputMode="decimal" value={f.amount ?? ""} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-11 w-full min-w-0 mt-1 tabular-nums" data-testid="pay-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs" htmlFor="pay-no">Číslo faktury</Label>
            <Input id="pay-no" value={f.invoice_no || ""} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} placeholder="např. 2026-0042" className="h-11 mt-1" data-testid="pay-invoice-no" />
          </div>
          <div className="rounded-[12px] bg-secondary px-3.5 py-3 text-sm" data-testid="pay-preview">
            {b.paid_until
              ? <>Zaplaceno do <b>{day(b.paid_until)}</b> → <b className="text-success">{target ? day(target) : "—"}</b></>
              : <>Zaplacené období se dopočítá od data platby{f.custom && f.paid_until ? <> → <b className="text-success">{day(f.paid_until)}</b></> : null}.</>}
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Jiné datum „zaplaceno do“</span>
            <Switch checked={!!f.custom} onCheckedChange={(v) => setF({ ...f, custom: v, paid_until: v ? (b.next_end || "") : "" })} data-testid="pay-custom" />
          </label>
          {f.custom && (
            <Input type="date" value={f.paid_until || ""} onChange={(e) => setF({ ...f, paid_until: e.target.value })} className="h-11 w-full min-w-0" aria-label="Zaplaceno do" data-testid="pay-custom-until" />
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Zrušit</Button>
            <Button disabled={busy || !f.paid_on || (f.custom && !f.paid_until)} onClick={submit} className="w-full sm:w-auto font-bold" data-testid="pay-submit">Zaevidovat</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PostponeDialog({ open, onOpenChange, b, onSubmit, busy }) {
  const [until, setUntil] = useState("");
  useEffect(() => { if (open) setUntil(b.lock_postponed_until || plusDays(todayIso(), 7)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="sub-postpone-dialog">
        <DialogHeader>
          <DialogTitle>Odložit zámek</DialogTitle>
          <DialogDescription>Zákazník slíbil zaplatit? Do tohoto dne se přístup nezamkne.</DialogDescription>
        </DialogHeader>
        <Input type="date" value={until} min={todayIso()} onChange={(e) => setUntil(e.target.value)} className="h-11 w-full min-w-0" aria-label="Odložit do" data-testid="postpone-until" />
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
          {b.lock_postponed_until && <Button variant="outline" disabled={busy} onClick={() => onSubmit(null)} className="w-full sm:w-auto" data-testid="postpone-clear">Zrušit odklad</Button>}
          <Button disabled={busy || !until} onClick={() => onSubmit(until)} className="w-full sm:w-auto font-bold" data-testid="postpone-save">Odložit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Napojení Fakturoid (dialog z přehledu Superadminu) ----------
export function FakturoidDialog({ open, onOpenChange }) {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ slug: "", client_id: "", client_secret: "" });
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  useEffect(() => {
    if (!open) return;
    setTest(null);
    api.get("/admin/fakturoid").then((r) => { setS(r.data); setF({ slug: r.data.slug || "", client_id: "", client_secret: "" }); }).catch(() => setS({}));
  }, [open]);
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = {};
      if (f.slug.trim() !== (s?.slug || "")) body.slug = f.slug.trim();
      if (f.client_id) body.client_id = f.client_id;
      if (f.client_secret) body.client_secret = f.client_secret;
      Object.assign(body, extra);
      const { data } = await api.post("/admin/fakturoid", body);
      setS(data); setF({ slug: data.slug || "", client_id: "", client_secret: "" });
      if (data.test) setTest(data.test);
      if (!data.test) toast.success("Uloženo");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fakturoid/test"); setTest(data);
      if (data.slug) { const r = await api.get("/admin/fakturoid"); setS(r.data); setF((x) => ({ ...x, slug: r.data.slug || "" })); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async (text, what) => { try { await navigator.clipboard.writeText(text); toast.success(`${what} zkopírováno`); } catch { toast.error("Kopírování se nepovedlo"); } };
  const configured = !!s?.configured;
  const hasCreds = !!s?.client_id_hint;   // přístup uložený, zkratka účtu se dohledá (208)
  const canSave = (f.client_id && f.client_secret) || (hasCreds && f.slug.trim() !== (s?.slug || ""));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fakturoid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Napojení Fakturoid</DialogTitle>
          <DialogDescription>Faktury za předplatné se vystaví samy 7 dní před koncem období a platby se spárují samy.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          {test
            ? (test.ok
              ? <Banner tone="ok" icon={CheckCircle2} testid="fa-test-ok">Spojeno s účtem <b>{test.account}</b>{test.slug ? <> — zkratku <b>{test.slug}</b> jsem doplnil sám</> : null}.</Banner>
              : <Banner tone="danger" icon={AlertTriangle} testid="fa-test-fail">Spojení selhalo: {test.error}</Banner>)
            : configured
              ? <Banner tone="ok" icon={CheckCircle2} testid="fa-configured">Napojeno{s.account_name ? <> na účet <b>{s.account_name}</b></> : null} · Client ID {s.client_id_hint}.</Banner>
              : <Banner tone="warn" icon={Link2} testid="fa-not-configured">Zatím nenapojeno — faktury ti budou chodit jako e-mail „Vystav fakturu“.</Banner>}

          {test && !test.ok && test.accounts?.length > 0 && (
            <div data-testid="fa-accounts">
              <div className="text-xs text-muted-foreground mb-1.5">Vyber účet, na který se mají vystavovat faktury:</div>
              <div className="flex flex-wrap gap-2">
                {test.accounts.map((a) => (
                  <Button key={a.slug} type="button" variant="outline" size="sm" disabled={busy} className="max-w-full"
                    onClick={() => { setF({ ...f, slug: a.slug }); save({ slug: a.slug }); }} data-testid={`fa-account-${a.slug}`}>
                    <span className="truncate">{a.name} · {a.slug}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-1"><Label className="text-xs" htmlFor="fa-slug">Účet</Label>
              <InfoTip label="Kde najdu účet">Zkratka z adresy za <b>app.fakturoid.cz/</b> — u app.fakturoid.cz/petrbradna/… je to <b>petrbradna</b>. Když ji nevíš, nech pole prázdné nebo napiš název účtu — po uložení se dohledá sama.</InfoTip></div>
            <Input id="fa-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="nech prázdné — doplní se samo" className="h-11 mt-1" autoComplete="off" data-testid="fa-slug" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-1"><Label className="text-xs" htmlFor="fa-cid">Client ID</Label>
                <InfoTip label="Kde najdu Client ID a Secret">Fakturoid › vpravo nahoře tvoje jméno › <b>Uživatelský účet</b> › API v3 — vytvoř přístup a zkopíruj Client ID a Client Secret sem.</InfoTip></div>
              <Input id="fa-cid" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} placeholder={configured ? `uloženo ${s.client_id_hint || ""}` : ""} className="h-11 mt-1 w-full min-w-0" autoComplete="off" data-testid="fa-client-id" />
            </div>
            <div className="min-w-0">
              <Label className="text-xs" htmlFor="fa-secret">Client Secret</Label>
              <Input id="fa-secret" type="password" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} placeholder={configured ? "uloženo — vyplň jen pro změnu" : ""} className="h-11 mt-1 w-full min-w-0" autoComplete="new-password" data-testid="fa-client-secret" />
            </div>
          </div>

          <label className="flex items-start justify-between gap-3 text-sm">
            <span><b>Odesílat faktury zákazníkům automaticky</b><span className="block text-xs text-muted-foreground">Vypnuto = faktura se jen vystaví, zkontroluješ ji a odešleš ve Fakturoidu.</span></span>
            <Switch checked={!!s?.auto_send} disabled={busy || !s} onCheckedChange={(v) => save({ auto_send: v })} data-testid="fa-auto-send" />
          </label>
          <label className="flex items-start justify-between gap-3 text-sm">
            <span><b>Doklad k platbám kartou</b><span className="block text-xs text-muted-foreground">Ke každé platbě přes Stripe vystaví zaplacenou fakturu.</span></span>
            <Switch checked={s?.stripe_receipts !== false} disabled={busy || !s} onCheckedChange={(v) => save({ stripe_receipts: v })} data-testid="fa-stripe-receipts" />
          </label>

          {configured && s.webhook_token && (
            <div className="rounded-[12px] border border-border p-3.5 space-y-2" data-testid="fa-webhook">
              <div className="flex items-center gap-1 text-sm font-bold">Webhook — platba se projeví hned
                <InfoTip label="Jak nastavit webhook">Fakturoid › Nastavení › Napojení › <b>Webhooky</b> › Přidat. Vlož adresu a autorizační hlavičku, vyber událost <b>invoice_paid</b>. Bez webhooku se aplikace na platby ptá jednou za hodinu.</InfoTip></div>
              {[["Adresa", s.webhook_url, "Adresa"], ["Autorizační hlavička", `Bearer ${s.webhook_token}`, "Hlavička"]].map(([l, v, w]) => (
                <div key={l} className="min-w-0">
                  <div className="text-xs text-muted-foreground">{l}</div>
                  <div className="flex gap-2 mt-0.5">
                    <Input readOnly value={v} onFocus={(e) => e.target.select()} className="h-10 text-xs w-full min-w-0 font-mono" />
                    <Button type="button" variant="outline" size="sm" className="h-10 shrink-0" onClick={() => copy(v, w)} aria-label={`Zkopírovat: ${l}`}><Copy size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            {hasCreds && <Button variant="outline" disabled={busy} onClick={verify} className="w-full sm:w-auto" data-testid="fa-verify">Ověřit spojení</Button>}
            <Button disabled={busy || !canSave} onClick={() => save()} className="w-full sm:w-auto font-bold" data-testid="fa-save">
              {configured ? "Uložit změny" : "Uložit a ověřit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Testovací účet (207) ----------
export function TestAccountCard({ c, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const send = async (is_test, cancel_stripe = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${c.id}/test`, { is_test, cancel_stripe });
      toast.success(is_test ? (data.stripe_canceled ? "Testovací účet · předplatné ve Stripe zrušeno" : "Označeno jako testovací účet") : "Testovací účet zrušen");
      setAsk(false);
      await onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = (v) => {
    if (v && c.stripe_subscription_id) { setAsk(true); return; }
    send(v);
  };
  return (
    <div className={`border rounded-[18px] p-4 ${c.is_test ? "bg-violet-500/10 border-violet-500/40" : "bg-card border-border"}`} data-testid="section-test-account">
      <label className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-sm font-bold">Testovací účet</span>
          <span className="block text-xs text-muted-foreground mt-0.5">Nepočítá se do MRR ani statistik, nikdy se nezamkne, žádné faktury ani e-maily o platbách.</span>
        </span>
        <Switch checked={!!c.is_test} disabled={busy} onCheckedChange={toggle} data-testid="test-account-switch" />
      </label>
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="max-w-md" data-testid="test-account-dialog">
          <DialogHeader>
            <DialogTitle>Zrušit i předplatné ve Stripe?</DialogTitle>
            <DialogDescription>{c.name} má aktivní předplatné kartou — strhávají se z něj peníze.</DialogDescription>
          </DialogHeader>
          <p className="text-sm">Zrušením se karta přestane strhávat hned. Už stržené platby vrátíš ve Stripe (Platby › platba › Vrátit).</p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="outline" disabled={busy} onClick={() => send(true, false)} className="w-full sm:w-auto" data-testid="test-only-flag">Jen označit</Button>
            <Button disabled={busy} onClick={() => send(true, true)} className="w-full sm:w-auto font-bold" data-testid="test-cancel-stripe">Označit a zrušit předplatné</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
